import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const DEFAULT_HYPERTENSION = {
  Screened: 20458837,
  "Re Screened": 7983741,
  Examined: 3349826,
  Diagnosed: 2423047,
  Treatment: 3404365,
  "Follow Up": 1104115,
  "Follow up Adherence": 488520,
};

const DEFAULT_DIABETES = {
  Screened: 20458837,
  "Re Screened": 7983741,
  Examined: 3349826,
  Diagnosed: 2423047,
  Treatment: 3404365,
  "Follow Up": 1104115,
  "Follow up</br>Adherence": 488520,
};

const HYP_COLOR = "#5e72e4";
const DIA_COLOR = "#11cdef";
const HYP_COLOR_DARK = "#3246b0";
const DIA_COLOR_DARK = "#0aa3c0";

export default function HealthNCDCharts({
  hypertension = DEFAULT_HYPERTENSION,
  diabetes = DEFAULT_DIABETES,
  selectedDistrict = null,
  height = 300,
}) {
  // Apply district seed logic: 20% for district view
  const hypData = useMemo(() => {
    if (!selectedDistrict) return hypertension;
    return Object.fromEntries(
      Object.entries(hypertension).map(([k, v]) => [k, Math.round(v * 0.2)])
    );
  }, [hypertension, selectedDistrict]);

  const diaData = useMemo(() => {
    if (!selectedDistrict) return diabetes;
    return Object.fromEntries(
      Object.entries(diabetes).map(([k, v]) => [k, Math.round(v * 0.2)])
    );
  }, [diabetes, selectedDistrict]);

  const labels = useMemo(() => Object.keys(hypData), [hypData]);

  const data = useMemo(() => {
    const hypValues = labels.map((k) => hypData[k] || 0);
    const diaValues = labels.map((k) => diaData[k] || 0);

    return {
      labels,
      datasets: [
        {
          label: "Hypertension",
          data: hypValues,
          backgroundColor: HYP_COLOR,
          borderColor: HYP_COLOR_DARK,
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 14,
        },
        {
          label: "Diabetes Mellitus",
          data: diaValues,
          backgroundColor: DIA_COLOR,
          borderColor: DIA_COLOR_DARK,
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 14,
        },
      ],
    };
  }, [labels, hypData, diaData]);

  const options = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          anchor: "end",
          align: "end",
          color: "black",
          font: { size: 11 },
          formatter: (value) => Number(value).toLocaleString(),
        },
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const value = ctx.parsed.x ?? ctx.parsed.y;
              return `${ctx.dataset.label}: ${Number(value).toLocaleString()}`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            callback: (value) => {
              const n = Number(value);
              if (n >= 10000000) return (n / 1000000).toFixed(1) + "M";
              if (n >= 100000) return (n / 1000).toFixed(0) + "k";
              return n.toLocaleString();
            },
            color: "#475569",
            font: { size: 11 },
          },
          grid: { color: "#f1f5f9" },
        },
        y: { ticks: { color: "#475569", font: { size: 12 } }, grid: { display: false } },
      },
      layout: { padding: { top: 8, right: 8, left: 8, bottom: 6 } },
    }),
    []
  );

  return (
    <div className="bg-white rounded-lg shadow-sm pr-3 pl-0 py-1 mt-2">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h5 className="classHeading text-center px-3 pt-2">Hypertension & Diabetes Mellitus Data</h5>
        </div>
      </div>

      <div style={{ height }} className="relative">
        <Bar data={data} options={options} />
      </div>
    </div>
  );
}
